// ————————————————————————————————————————————
// Media Slot Foundation
//
// This is the single place responsible for rendering media inside a
// diamond card. Diamond Card Foundation and Diamond Card Hover Experience
// are final — this file does not touch card layout, dimensions,
// typography, price, CTA, hover, the favorite heart, the 360° badge's
// visual design, the grid, or the placeholder's look. It only owns what
// already lived inline in diamond-api.js's card template: the primary
// media content (image or placeholder) and capability badges (360°) that
// sit inside `.diamond-card__media`.
//
// Four responsibilities are kept independent on purpose, so a future media
// module (360 Viewer, Super Zoom, HD Video, AI Analysis) can be added by
// extending the small registries below — never by adding branches to
// renderLiveDiamondResults() in diamond-api.js:
//
//   1. Media data         — resolveDiamondMedia()
//   2. Capability detect  — detectDiamondMediaCapabilities()
//   3. Media rendering    — buildDiamondCardMedia()
//   4. Media interaction  — attachDiamondMediaInteractions()
//
// Today two capabilities are real (image, interactive 360) and only one
// interaction mode exists (static). Interactive 360 is true when Nivoda
// v360 OR preparedTurntable metadata is playable (available + manifestUrl).
// Capability checks never fetch. Everything else below is a named,
// reserved extension point that resolves to "off" until a future module
// registers itself — nothing extra is exposed to the card today.
// ————————————————————————————————————————————
(function (global) {
  "use strict";

  function escapeHtml(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ));
  }

  function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
  }

  // Shared markup for the "no photo yet" state — also used as the runtime
  // fallback when a real image URL 404s/fails to decode, so a broken-image
  // icon or raw alt text never reaches the card. Moved here from
  // diamond-api.js because it's media rendering, not card assembly; markup
  // and CSS class are unchanged, so today's visuals are identical.
  const PLACEHOLDER_HTML = `<span class="diamond-card__placeholder" aria-hidden="true">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 24 L32 8 L48 24 L32 58 Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                  <path d="M16 24 L48 24 M25 24 L32 58 M39 24 L32 58 M22 24 L32 32 L42 24" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>
                </svg>
              </span>`;

  // Every interaction mode the slot can ever render into. Only STATIC ships
  // today; the rest are named now so future work (hover preview, click
  // preview, modal viewer, inline viewer) is a config value on this same
  // slot, not a new prop/branch invented on the card.
  const MODES = {
    STATIC: "static",
    HOVER_PREVIEW: "hover-preview",
    CLICK_PREVIEW: "click-preview",
    MODAL_VIEWER: "modal-viewer",
    INLINE_VIEWER: "inline-viewer",
  };
  const DEFAULT_MODE = MODES.STATIC;

  // Every media capability the slot can ever surface. Only IMAGE and V360
  // are real today — the rest are reserved so future modules (Super Zoom,
  // HD Video, AI Analysis) have a named slot to detect into later without
  // the card renderer needing to know they exist yet.
  //
  // CAPABILITIES.V360 names the existing "360°" affordance (badge). It is
  // true for interactive 360 from either Nivoda v360 or preparedTurntable —
  // not a second badge type, and not a fake Nivoda v360 object.
  const CAPABILITIES = {
    IMAGE: "image",
    V360: "v360",
    SUPER_ZOOM: "superZoom",
    VIDEO: "video",
    AI_ANALYSIS: "aiAnalysis",
  };

  // ————————————————————————————————————————————
  // 1) Media data
  //
  // Normalizes whatever raw diamond fields exist today into a stable media
  // descriptor. Callers never read d.image / d.v360 directly for rendering
  // purposes — they go through this so a new media field arriving later
  // (superZoom, video, aiAnalysis) means adding one line here, not hunting
  // through the card renderer.
  //
  // preparedTurntable is pass-through metadata only (same reference). It is
  // never fetched here; v360 remains raw Nivoda metadata or null.
  // ————————————————————————————————————————————
  function resolveDiamondMedia(diamond) {
    diamond = diamond || {};
    return {
      image: diamond.image || null,
      v360: (diamond.v360 && diamond.v360.url) ? diamond.v360 : null,
      preparedTurntable: diamond.preparedTurntable || null,
      // Reserved for future modules. Always present, always inert today —
      // the detectors below simply have nothing to find yet.
      superZoom: diamond.superZoom || null,
      video: diamond.video || null,
      aiAnalysis: diamond.aiAnalysis || null,
    };
  }

  // Canonical 360 capability (metadata only — never fetches).
  // Accepts a mapped diamond or a resolveDiamondMedia() descriptor.
  function hasPreparedTurntable(source) {
    const prepared = source && source.preparedTurntable;
    return !!(
      prepared &&
      prepared.available === true &&
      isNonEmptyString(prepared.manifestUrl)
    );
  }

  function hasInteractive360(source) {
    const v360 = source && source.v360;
    return Boolean((v360 && v360.url) || hasPreparedTurntable(source));
  }

  // ————————————————————————————————————————————
  // 2) Media capability detection
  //
  // One small function per capability, looked up by name instead of an
  // if/else chain. Adding a capability later means adding one entry to
  // this table — never touching the renderer or the interaction layer.
  // ————————————————————————————————————————————
  const CAPABILITY_DETECTORS = {
    [CAPABILITIES.IMAGE]: (media) => Boolean(media.image),
    [CAPABILITIES.V360]: (media) => hasInteractive360(media),
    [CAPABILITIES.SUPER_ZOOM]: () => false,
    [CAPABILITIES.VIDEO]: () => false,
    [CAPABILITIES.AI_ANALYSIS]: () => false,
  };

  function detectDiamondMediaCapabilities(media) {
    media = media || {};
    const capabilities = {};
    Object.keys(CAPABILITY_DETECTORS).forEach((key) => {
      capabilities[key] = Boolean(CAPABILITY_DETECTORS[key](media));
    });
    return capabilities;
  }

  // ————————————————————————————————————————————
  // 3) Media rendering
  //
  // Primary content (what fills the slot) and overlays (badges layered on
  // top of it) are resolved separately, each by lookup table. This is how
  // a future mode or capability plugs in: register a renderer, don't
  // branch inside buildDiamondCardMedia() or the card template.
  // ————————————————————————————————————————————

  // Only STATIC exists today. A future mode (e.g. hover-preview swapping to
  // a 360 spin frame on :hover) registers its own primary renderer here
  // instead of editing this table or the card renderer.
  const PRIMARY_RENDERERS = {
    [MODES.STATIC]: function renderStaticPrimary(media, context) {
      if (media.image) {
        const alt = escapeHtml((context && context.alt) || "");
        return `<img class="diamond-card__image" src="${escapeHtml(media.image)}" alt="${alt}" loading="lazy">`;
      }
      return PLACEHOLDER_HTML;
    },
  };

  // Capability -> badge overlay. Ordered array (not a plain object) so
  // stacking order is explicit if more than one overlay is ever active at
  // once. Each entry only needs a capability key and a render function —
  // the card never needs to know these exist.
  const OVERLAY_RENDERERS = [
    {
      capability: CAPABILITIES.V360,
      render: function renderBadge360() {
        return `<span class="diamond-card__badge-360" aria-hidden="true">360\u00b0</span>`;
      },
    },
    // Reserved: Super Zoom / HD Video / AI Analysis badges register here,
    // each independent of the others and of the primary renderer above.
  ];

  // The one function diamond-api.js calls to fill `.diamond-card__media`.
  // `options.mode` selects the interaction mode (defaults to static);
  // `options.alt` carries the accessible label the card already computed.
  function buildDiamondCardMedia(media, options) {
    media = media || {};
    options = options || {};
    const mode = PRIMARY_RENDERERS[options.mode] ? options.mode : DEFAULT_MODE;
    const capabilities = detectDiamondMediaCapabilities(media);

    const primaryHtml = PRIMARY_RENDERERS[mode](media, options);
    const overlaysHtml = OVERLAY_RENDERERS
      .filter((overlay) => capabilities[overlay.capability])
      .map((overlay) => overlay.render(media, options))
      .join("");

    return primaryHtml + overlaysHtml;
  }

  // ————————————————————————————————————————————
  // 4) Media interaction
  //
  // Wires up behavior for whatever buildDiamondCardMedia() just rendered,
  // keyed by the same mode. Today STATIC only needs the broken-image ->
  // placeholder fallback; a future hover/click/modal/inline mode attaches
  // its own listeners here instead of the card renderer growing new event
  // wiring for each one.
  // ————————————————————————————————————————————
  const INTERACTION_HANDLERS = {
    [MODES.STATIC]: function attachStaticInteractions(slotEl) {
      const imageEl = slotEl.querySelector(".diamond-card__image");
      if (!imageEl) return;
      // If a real image URL fails to load (404, decode error, etc.), swap
      // it for the same diamond-outline placeholder used when there's no
      // image at all — never a broken-image icon or raw alt text.
      imageEl.addEventListener(
        "error",
        () => {
          imageEl.outerHTML = PLACEHOLDER_HTML;
        },
        { once: true }
      );
    },
  };

  // Call once per card after its media markup is in the DOM. `slotEl` is
  // the element buildDiamondCardMedia()'s output was inserted into (i.e.
  // `.diamond-card__media`).
  function attachDiamondMediaInteractions(slotEl, media, options) {
    if (!slotEl) return;
    options = options || {};
    const mode = INTERACTION_HANDLERS[options.mode] ? options.mode : DEFAULT_MODE;
    const handler = INTERACTION_HANDLERS[mode];
    if (handler) handler(slotEl, media, options);
  }

  global.DiamondCardMedia = {
    MODES,
    CAPABILITIES,
    resolveDiamondMedia,
    hasPreparedTurntable,
    hasInteractive360,
    detectDiamondMediaCapabilities,
    buildDiamondCardMedia,
    attachDiamondMediaInteractions,
  };
})(window);
